import React, {Fragment} from 'react'
import styles from './TripMetrics.scss'
import {FullBleedList} from 'features/routes/RouteShowPage/SidePanel/FullBleedList'
import {Duration, Distance, Elevation, Grade, Speed, Pace} from 'common/units'
import _ from 'lodash'
import {TrackLocation} from 'data/records'
import {featureTypes} from 'common/MapV2/featureTypes'
import cx from 'classnames'

const TripMetricRow = ({label, value, onMouseOver, onMouseOut}) => (
  <li onMouseOver={onMouseOver} onMouseOut={onMouseOut}>
    <div>{label}</div>
    <div><strong>{value}</strong></div>
  </li>
)

// https://ridewithgps.com/trips/1476234 is useful for testing this!
export class TripMetrics extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      sliceMetrics: null
    }
  }

  componentDidMount () {
    this.initData()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.selectedFeature !== this.props.selectedFeature) {
      this.calculateMetrics()
    }
  }

  initData () {
    // init rwgps objects
    const {trip, selectedFeature} = this.props
    const tpsResult = window.rwgps.TrackPoint.fromJSON(trip.rawPoints)
    const points = tpsResult.trackPoints
    const data = new window.rwgps.Data(points)
    this._data = data
    selectedFeature && this.getHighlightSliceIndexes() && this.calculateMetrics()
  }

  pointMetricFeature = (label, index) => {
    const {trip, selectedFeature} = this.props
    // if we have metrics for a highlighted segment, need to pad highlightPoint
    // index out by left index of the highlightSlice
    const highlightSlice = _.get(selectedFeature, 'data.highlightSlice')
    const idx = highlightSlice ? index + highlightSlice[0].left : index

    return {
      type: featureTypes.POINT_METRIC,
      data: {highlightPoint: {trackPoint: trip.trackPoints[idx], index: idx}, label}
    }
  }

  getHighlightSliceIndexes = () => {
    const {selectedFeature} = this.props
    const highlightSlice = _.get(selectedFeature, 'data.highlightSlice')
    if (!highlightSlice) {
      return null
    }
    return highlightSlice.map(TrackLocation.closestIndex)
  }

  calculateMetrics = _.throttle(() => {
    const sliceIndexes = this.getHighlightSliceIndexes()
    if (!sliceIndexes) {
      this.setState({sliceMetrics: null})
      return
    }
    // old RSP includes the last point in the highlight slice when it does metrics calculation
    const dataSlice = this._data.slice(sliceIndexes[0], sliceIndexes[1] - sliceIndexes[0] + 1)
    const mc = new window.rwgps.MetricsCalculator(dataSlice, false)
    const sliceMetrics = mc.calculate()
    this.setState({sliceMetrics})
  }, 200, {leading: true, trailing: true})

  render () {
    // _trip is my way of indicating that we shouldn't directly read anything
    // off the _trip object here, since it should all flow the metrics var
    // (which can either be _trip.metrics OR the highlightSlice metrics)
    const {trip: _trip, onFeatureHovered} = this.props
    const {sliceMetrics} = this.state
    if (!_trip.metrics) {
      return null
    }

    const showSegmentMetrics = this.getHighlightSliceIndexes() && sliceMetrics

    const metrics = showSegmentMetrics
      ? sliceMetrics
      : _trip.metrics

    // get metric for use as a string
    const m = (path) => Math.round(_.get(metrics, path)) || '--'

    // get metric for use as a numeric input to a formatter
    const v = (path) => _.get(metrics, path, 0)

    return (
      <div className={cx(styles.TripMetrics, showSegmentMetrics && styles.showingSegment)}>
        <h2>Metrics</h2>
        <FullBleedList interactive={false}>
          <TripMetricRow
            label='Distance'
            value={<Distance short value={v('distance')} />} />
          <TripMetricRow
            label='Elevation'
            value={<Fragment>
              + <Elevation short value={v('ele_gain')} /> /
              {' '}-<Elevation short value={v('ele_loss')} />
            </Fragment>} />
          <Grade short value={v('grade.max')}>
            {m => <TripMetricRow
              onMouseOver={() => onFeatureHovered(this.pointMetricFeature(`Max Grade: ${m.valueToString()}%`, v('grade.max_i')))}
              onMouseOut={() => onFeatureHovered(null)}
              label='Max Grade'
              value={m.valueToString() + '%'} />}
          </Grade>
          <TripMetricRow
            label='Avg Grade'
            value={<Grade short value={v('grade.avg')} />} />
          <li className={styles.hr} />

          <TripMetricRow
            label='Total Duration'
            value={<Duration valueOnly biggestUnits value={v('duration')} />} />
          <TripMetricRow
            label='Moving Time'
            value={<Duration valueOnly biggestUnits value={v('movingTime')} />} />
          <TripMetricRow
            label='Stopped Time'
            value={<Duration valueOnly biggestUnits value={v('stoppedTime')} />} />
          <li className={styles.hr} />

          <Speed short bigUnits value={v('speed.max')}>
            {m => <TripMetricRow
              onMouseOver={() => onFeatureHovered(this.pointMetricFeature(`Max Speed: ${m.valueToString()} ${m.short}`, v('speed.max_i')))}
              onMouseOut={() => onFeatureHovered(null)}
              label='Max Speed'
              value={`${m.valueToString()} ${m.short}`} />}
          </Speed>
          <TripMetricRow
            label='Avg Speed'
            value={<Speed short bigUnits value={v('speed.avg')} />} />
          <li className={styles.hr} />

          <TripMetricRow
            label='VAM'
            value={m('vam') + ' Vm/h'} />
          {/* Why is ascentTime camelCase when all the other metrics are snake_case?? */}
          <TripMetricRow
            label='Ascent Time'
            value={<Duration valueOnly biggestUnits value={v('ascentTime')} />} />
          <TripMetricRow
            label='Descent Time'
            value={<Duration valueOnly biggestUnits value={v('descentTime')} />} />
          <li className={styles.hr} />

          <TripMetricRow
            label='Pace'
            value={<Pace valueOnly value={v('pace')} />} />
          <TripMetricRow
            label='Moving Pace'
            value={<Pace valueOnly value={v('movingPace')} />} />
          <li className={styles.hr} />

          {metrics.hr && metrics.hr.avg && <Fragment>
            {metrics.hr.max && <TripMetricRow
              onMouseOver={() => onFeatureHovered(this.pointMetricFeature(`Max Heart Rate: ${m('hr.max')} bpm`, v('hr.max_i')))}
              onMouseOut={() => onFeatureHovered(null)}
              label='Max Heart Rate'
              value={m('hr.max') + ' bpm'} />}
            {/* compare with undefined here because some sensors send 0 hr */}
            {metrics.hr.min !== undefined && <TripMetricRow
              label='Min Heart Rate'
              value={m('hr.min') + ' bpm'} />}
            {metrics.hr.avg && <TripMetricRow
              label='Avg Heart Rate'
              value={m('hr.avg') + ' bpm'} />}
            <li className={styles.hr} />
          </Fragment>}

          {metrics.hr_zones && <Fragment>
            <TripMetricRow
              label='HR Zone 1'
              value={<Duration short valueOnly biggestUnits value={metrics.hr_zones['1']} />} />
            <TripMetricRow
              label='HR Zone 2'
              value={<Duration short valueOnly biggestUnits value={metrics.hr_zones['2']} />} />
            <TripMetricRow
              label='HR Zone 3'
              value={<Duration short valueOnly biggestUnits value={metrics.hr_zones['3']} />} />
            <TripMetricRow
              label='HR Zone 4'
              value={<Duration short valueOnly biggestUnits value={metrics.hr_zones['4']} />} />
            <TripMetricRow
              label='HR Zone 5'
              value={<Duration short valueOnly biggestUnits value={metrics.hr_zones['5']} />} />
            <li className={styles.hr} />
          </Fragment>}

          {!showSegmentMetrics && _trip.calories && <TripMetricRow
            label='Calories'
            value={_trip.calories} />}

          {metrics.watts && metrics.watts.avg && <Fragment>
            {metrics.watts.max && <TripMetricRow
              label='Max Watts'
              onMouseOver={() => onFeatureHovered(this.pointMetricFeature(`Max Watts: ${m('watts.max')} watts`, v('watts.max_i')))}
              onMouseOut={() => onFeatureHovered(null)}
              value={m('watts.max') + ' watts'} />}
            {/* metrics calculator doesn't provide avg power estimated, that comes from the trip model
                so we need to hide this if there's a slice selected and no real power data */}
            {(metrics.watts.avg || !showSegmentMetrics) && <TripMetricRow
              label={metrics.watts.avg ? 'Avg Watts' : 'Avg Watts (estimated)'}
              value={metrics.watts.avg
                ? m('watts.avg') + ' watts'
                : <i>{Math.round(_trip.avgPowerEstimated)} watts</i>} />}
            {metrics.watts.normalized && <TripMetricRow
              label='WR Power'
              value={Math.round(metrics.watts.normalized) + ' watts'} />}
            {metrics.kJ && <TripMetricRow
              label='Work'
              value={m('kJ') + ' kJ'} />}
            <li className={styles.hr} />
          </Fragment>}

          {metrics.cad && metrics.cad.avg && <Fragment>
            {metrics.cad.max && <TripMetricRow
              label='Max Cadence'
              onMouseOver={() => onFeatureHovered(this.pointMetricFeature(`Max Cadence: ${metrics.cad.max} rpm`, v('cad.max_i')))}
              onMouseOut={() => onFeatureHovered(null)}
              value={metrics.cad.max + ' rpm'} />}
            {metrics.cad.min && <TripMetricRow
              label='Min Cadence'
              value={metrics.cad.min + ' rpm'} />}
            {metrics.cad.avg && <TripMetricRow
              label='Avg Cadence'
              value={Math.round(metrics.cad.avg) + ' rpm'} />}
            <li className={styles.hr} />
          </Fragment>}

        </FullBleedList>
      </div>
    )
  }
}
